import React, { useMemo } from 'react';
import Modal from './Modal';
import { useTimeClock } from '../hooks/useTimeClock';
import { LeaveBalance } from '../../shared/types.js';

interface LeaveDetailsModalProps {
    isOpen: boolean;
    onClose: () => void;
    employeeId: string;
    balance: LeaveBalance;
}

interface LeaveDayInfo {
    date: Date;
    hours: number;
}

const LeaveDetailsModal: React.FC<LeaveDetailsModalProps> = ({ isOpen, onClose, employeeId, balance }) => {
    const { leaveEntries } = useTimeClock();

    const leaveDays = useMemo(() => {
        if (!balance.leaveTypeId || !employeeId) return [];

        const currentYear = new Date().getFullYear();

        const relevantEntries = leaveEntries.filter(entry => 
            entry.leaveCategoryId === balance.leaveTypeId && entry.employeeIds.includes(employeeId)
        );

        const allDays: LeaveDayInfo[] = [];

        for (const entry of relevantEntries) {
            let currentDate = new Date(entry.startDate);
            const endDate = new Date(entry.endDate);

            while (currentDate.getTime() <= endDate.getTime()) {
                const dayOfWeek = currentDate.getUTCDay();
                const isSkipped = ((entry.skipSaturday ?? true) && dayOfWeek === 6) || ((entry.skipSunday ?? true) && dayOfWeek === 0);
                
                if (!isSkipped && currentDate.getUTCFullYear() === currentYear) {
                    const displayDate = new Date(currentDate.getUTCFullYear(), currentDate.getUTCMonth(), currentDate.getUTCDate());
                    allDays.push({
                        date: displayDate,
                        hours: entry.hoursPerDay,
                    });
                }
                currentDate.setUTCDate(currentDate.getUTCDate() + 1);
            }
        }

        return allDays.sort((a, b) => a.date.getTime() - b.date.getTime());
    }, [leaveEntries, balance.leaveTypeId, employeeId]);
    
    const formatLeaveAmount = (hours: number): string => {
        if (hours === 8) return "1 Day";
        if (hours === 4) return "1/2 Day";
        return `${hours} hours`;
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={`Leave Details for ${balance.leaveTypeName}`}>
            <div className="text-gray-900 dark:text-white space-y-4">
                <div className="flex justify-between items-center p-3 bg-green-100 dark:bg-green-900/50 rounded-md">
                    <span className="font-bold text-green-800 dark:text-green-300">Leave Entitlement</span>
                    <span className="text-2xl font-bold text-green-700 dark:text-green-200">{balance.entitled.toFixed(2)} Days</span>
                </div>

                {leaveDays.length > 0 ? (
                    <ul className="space-y-2 max-h-64 overflow-y-auto border border-gray-200 dark:border-gray-700 p-2 rounded-md">
                        {leaveDays.map((day, index) => (
                            <li key={index} className="bg-red-100 dark:bg-red-900/50 rounded-md font-medium text-red-800 dark:text-red-200">
                                — {formatLeaveAmount(day.hours)}, {day.date.toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                            </li>
                        ))}
                    </ul>
                ) : (
                    <p className="text-center text-gray-500 dark:text-gray-400 p-8">No leave days have been used in the current year.</p>
                )}
                
                <hr className="border-gray-300 dark:border-gray-600 my-4" />

                <div className="flex justify-between items-center p-3 bg-blue-100 dark:bg-blue-900/50 rounded-md">
                    <span className="font-bold text-blue-800 dark:text-blue-300">Remaining Balance</span>
                    <span className="text-2xl font-bold text-blue-700 dark:text-blue-200">{balance.available.toFixed(2)} Days</span>
                </div>
            </div>
        </Modal>
    );
};

export default LeaveDetailsModal;